import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Check,
  Bot,
  Settings2,
  Sparkles,
  FileText,
  Cpu,
  Rocket,
  X,
  Layers
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { FluentCard } from '@/components/layout/page-header'
import { useToast } from '@/components/ui/use-toast'
import { useConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  type CreateAgentFromScratchRequest,
  type CustomPersonalityTraitsInput,
  type AgentType,
  type SkillLibraryItem,
} from '@/lib/api/services/aiStudio'
import { useCreateAgentFromScratch, useCustomAgent, useUpdateCustomAgent } from '@/lib/hooks/use-ai-studio'

// Import step components
import { BasicInfoStep } from './builder-steps/BasicInfoStep'
import { ScopeStep } from './builder-steps/ScopeStep'
import { SkillsStep } from './builder-steps/SkillsStep'
import { PersonalityStep } from './builder-steps/PersonalityStep'
import { SystemPromptStep } from './builder-steps/SystemPromptStep'
import { ModelStep } from './builder-steps/ModelStep'
import { TestDeployStep } from './builder-steps/TestDeployStep'

// Local storage key for draft state
const DRAFT_STORAGE_KEY = 'ai-studio-agent-draft'

// Step configuration
const STEPS = [
  { id: 1, title: 'Basic Info', icon: Bot, description: 'Name and description' },
  { id: 2, title: 'Scope', icon: Settings2, description: 'Access and permissions' },
  { id: 3, title: 'Skills', icon: Layers, description: 'Capabilities' },
  { id: 4, title: 'Personality', icon: Sparkles, description: 'Tone and style' },
  { id: 5, title: 'Prompt', icon: FileText, description: 'System instructions' },
  { id: 6, title: 'Model', icon: Cpu, description: 'AI configuration' },
  { id: 7, title: 'Test & Deploy', icon: Rocket, description: 'Validate and publish' },
]

// Agent state interface
export interface AgentBuilderState {
  // Basic Info
  name: string
  slug: string
  description: string
  agentType: AgentType
  emoji: string
  color: string

  // Scope
  accessLevel: 'private' | 'team' | 'organization'
  documentTypes: string[]
  canAccessContent: boolean
  canAccessData: boolean

  // Skills
  selectedSkills: SkillLibraryItem[]

  // Personality
  personality: CustomPersonalityTraitsInput

  // System Prompt
  systemPrompt: string
  useTemplate: boolean
  templateId?: string

  // Model
  primaryModel: string
  fallbackModel: string
  temperature: number
  maxResponseTokens: number

  // Meta
  isPublic: boolean
  isDraft: boolean
}

const initialState: AgentBuilderState = {
  name: '',
  slug: '',
  description: '',
  agentType: 'General',
  emoji: '🤖',
  color: 'purple',

  accessLevel: 'private',
  documentTypes: [],
  canAccessContent: true,
  canAccessData: false,

  selectedSkills: [],

  personality: {
    tone: 'professional',
    style: 'concise',
    expertise: [],
    language: 'en',
    formalityLevel: 7,
    creativityLevel: 5,
    verbosityLevel: 5,
  },

  systemPrompt: '',
  useTemplate: false,

  primaryModel: 'gpt-4o',
  fallbackModel: 'gpt-4o-mini',
  temperature: 0.7,
  maxResponseTokens: 2048,

  isPublic: false,
  isDraft: true,
}

export default function AgentBuilder() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const presetId = searchParams.get('preset')
  const { toast } = useToast()
  const { confirm, ConfirmDialogComponent } = useConfirmDialog()

  const [currentStep, setCurrentStep] = useState(1)
  const [state, setState] = useState<AgentBuilderState>(initialState)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  const isEditing = !!id

  // React Query hooks
  const { data: existingAgent } = useCustomAgent(id ?? '', {
    enabled: !!id,
  })

  const createMutation = useCreateAgentFromScratch({
    onSuccess: (agent) => {
      localStorage.removeItem(DRAFT_STORAGE_KEY)
      toast({
        title: 'Agent Created',
        description: `${agent.name} has been deployed successfully.`,
      })
      navigate('/ai-studio')
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to deploy agent',
        variant: 'destructive',
      })
    },
  })

  const updateMutation = useUpdateCustomAgent({
    onSuccess: (agent) => {
      toast({
        title: 'Agent Updated',
        description: `${agent.name} has been updated successfully.`,
      })
      navigate('/ai-studio')
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update agent',
        variant: 'destructive',
      })
    },
  })

  const saving = createMutation.isPending || updateMutation.isPending

  // Load existing agent when editing
  useEffect(() => {
    if (existingAgent && isEditing) {
      setState({
        name: existingAgent.name,
        slug: existingAgent.slug,
        description: existingAgent.description || '',
        agentType: existingAgent.agentType,
        emoji: '🤖',
        color: 'purple',
        accessLevel: existingAgent.isPublic ? 'organization' : 'private',
        documentTypes: [],
        canAccessContent: true,
        canAccessData: false,
        selectedSkills: existingAgent.skills.map(s => ({
          id: s.skillId,
          slug: '',
          name: s.name,
          description: '',
          category: s.category,
          systemPromptFragment: '',
          requiresApiKey: false,
          isBuiltIn: false,
          usageCount: 0,
        })),
        personality: existingAgent.personalityTraits || initialState.personality,
        systemPrompt: existingAgent.systemPrompt,
        useTemplate: false,
        primaryModel: existingAgent.primaryModel || 'gpt-4o',
        fallbackModel: existingAgent.fallbackModel || 'gpt-4o-mini',
        temperature: existingAgent.temperature || 0.7,
        maxResponseTokens: existingAgent.maxResponseTokens || 2048,
        isPublic: existingAgent.isPublic,
        isDraft: existingAgent.deploymentStatus === 'Draft',
      })
    }
  }, [existingAgent, isEditing])

  // Load draft from localStorage on mount
  useEffect(() => {
    if (!isEditing && !presetId) {
      const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY)
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft)
          setState(draft)
          toast({
            title: 'Draft Restored',
            description: 'Your previous progress has been restored.',
          })
        } catch {
          // Invalid draft, ignore
        }
      }
    }
  }, [isEditing, presetId, toast])

  // Auto-save draft on state change
  useEffect(() => {
    if (!isEditing && state.name) {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(state))
    }
  }, [state, isEditing])

  // Generate slug from name
  useEffect(() => {
    if (!isEditing) {
      const slug = state.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
      setState((prev) => ({ ...prev, slug }))
    }
  }, [state.name, isEditing])

  const updateState = useCallback((updates: Partial<AgentBuilderState>) => {
    setState((prev) => ({ ...prev, ...updates }))
    setValidationErrors({})
  }, [])

  const validateStep = useCallback((step: number): boolean => {
    const errors: Record<string, string> = {}

    switch (step) {
      case 1:
        if (!state.name.trim()) errors.name = 'Name is required'
        if (state.name.length > 50) errors.name = 'Name must be under 50 characters'
        break
      case 5:
        if (!state.systemPrompt.trim()) errors.systemPrompt = 'System prompt is required'
        if (state.systemPrompt.length < 20) errors.systemPrompt = 'System prompt should be at least 20 characters'
        break
      case 6:
        if (!state.primaryModel) errors.primaryModel = 'Primary model is required'
        break
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [state])

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep < STEPS.length) {
        setCurrentStep((prev) => prev + 1)
      }
    }
  }

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1)
    }
  }

  const handleSaveDraft = () => {
    try {
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(state))
      toast({
        title: 'Draft Saved',
        description: 'Your agent draft has been saved.',
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save draft',
        variant: 'destructive',
      })
    }
  }

  const handleDeploy = async () => {
    if (!validateStep(currentStep)) return

    const request: CreateAgentFromScratchRequest = {
      slug: state.slug,
      name: state.name,
      description: state.description,
      agentType: state.agentType,
      systemPrompt: state.systemPrompt,
      personalityTraits: state.personality,
      primaryModel: state.primaryModel,
      fallbackModel: state.fallbackModel,
      maxResponseTokens: state.maxResponseTokens,
      temperature: state.temperature,
      isPublic: state.isPublic,
      skillIds: state.selectedSkills.map((s) => s.id),
    }

    if (isEditing && id) {
      // Update existing agent
      updateMutation.mutate({
        id,
        data: {
          name: state.name,
          description: state.description,
          systemPrompt: state.systemPrompt,
          personalityTraits: state.personality,
          primaryModel: state.primaryModel,
          fallbackModel: state.fallbackModel,
          maxResponseTokens: state.maxResponseTokens,
          temperature: state.temperature,
          isPublic: state.isPublic,
        },
      })
    } else {
      // Create new agent
      createMutation.mutate(request)
    }
  }

  const handleCancel = async () => {
    if (state.name && !isEditing) {
      // Prompt to save draft
      const shouldSave = await confirm({
        title: 'Save Draft?',
        description: 'Would you like to save your progress as a draft before leaving?',
        confirmLabel: 'Save Draft',
        cancelLabel: 'Discard',
      })
      if (shouldSave) {
        handleSaveDraft()
      }
    }
    navigate('/ai-studio')
  }

  const progressPercent = ((currentStep - 1) / (STEPS.length - 1)) * 100

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {ConfirmDialogComponent}
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={handleCancel}>
              <X className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {isEditing ? 'Edit Agent' : 'Create New Agent'}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Step {currentStep} of {STEPS.length}: {STEPS[currentStep - 1]?.title ?? 'Unknown'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveDraft}
              disabled={saving || !state.name}
            >
              <Save className="mr-2 h-4 w-4" />
              Save Draft
            </Button>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white dark:bg-gray-800 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="w-full">
          <Progress value={progressPercent} className="h-1" />
        </div>
      </div>

      {/* Step Indicators */}
      <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="w-full">
          <div className="flex items-center justify-between">
            {STEPS.map((step) => {
              const Icon = step.icon
              const isActive = currentStep === step.id
              const isCompleted = currentStep > step.id

              return (
                <button
                  key={step.id}
                  onClick={() => isCompleted && setCurrentStep(step.id)}
                  disabled={!isCompleted}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                    isActive
                      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                      : isCompleted
                      ? 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer'
                      : 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <div
                    className={`p-1.5 rounded-lg ${
                      isActive
                        ? 'bg-purple-600 text-white'
                        : isCompleted
                        ? 'bg-emerald-500 text-white'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                    }`}
                  >
                    {isCompleted ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium">{step.title}</p>
                    <p className="text-xs opacity-70">{step.description}</p>
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="w-full p-6">
        <FluentCard className="p-8">
          {currentStep === 1 && (
            <BasicInfoStep
              state={state}
              updateState={updateState}
              errors={validationErrors}
            />
          )}

          {currentStep === 2 && (
            <ScopeStep state={state} updateState={updateState} />
          )}

          {currentStep === 3 && (
            <SkillsStep state={state} updateState={updateState} />
          )}

          {currentStep === 4 && (
            <PersonalityStep state={state} updateState={updateState} />
          )}

          {currentStep === 5 && (
            <SystemPromptStep
              state={state}
              updateState={updateState}
              errors={validationErrors}
            />
          )}

          {currentStep === 6 && (
            <ModelStep
              state={state}
              updateState={updateState}
              errors={validationErrors}
            />
          )}

          {currentStep === 7 && (
            <TestDeployStep
              state={state}
              onDeploy={handleDeploy}
              saving={saving}
            />
          )}
        </FluentCard>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="rounded-xl"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>

          <div className="flex items-center gap-3">
            {currentStep < STEPS.length ? (
              <Button
                onClick={handleNext}
                className="rounded-xl bg-purple-600 hover:bg-purple-700 text-white"
              >
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={handleDeploy}
                disabled={saving}
                className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {saving ? 'Deploying...' : 'Deploy Agent'}
                <Rocket className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
